import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle, User } from 'lucide-react';
import PropTypes from 'prop-types';
import UserService from '../../services/user.service';
import AppointmentService from '../../services/appoinment.service';

const AdminPatientAppointments = ({ patientId }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [therapists, setTherapists] = useState({});

  useEffect(() => {
    loadAppointments();
  }, [patientId]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const response = await AppointmentService.getAdminAppointments();
      
      // Filtra gli appuntamenti per il paziente specifico
      const patientAppointments = response.filter(
        appointment => appointment.patient_id.toString() === patientId.toString()
      );
      
      setAppointments(patientAppointments);
      
      // Carica le informazioni dei terapeuti associati agli appuntamenti
      await loadTherapistsInfo(patientAppointments);
    } catch (err) {
      console.error('Errore durante il caricamento degli appuntamenti:', err);
      setError('Impossibile caricare gli appuntamenti. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const loadTherapistsInfo = async (appointmentsList) => {
    try {
      const therapistIdsMap = {};
      appointmentsList.forEach(a => {
        therapistIdsMap[a.therapist_id] = true;
      });
      
      // Convertiamo le chiavi dell'oggetto in array
      const therapistIds = Object.keys(therapistIdsMap).map(id => parseInt(id, 10));
      
      const therapistsData = {};
      
      for (const id of therapistIds) {
        const therapistData = await UserService.getUserDetails(id);
        therapistsData[id] = therapistData;
      }
      
      setTherapists(therapistsData);
    } catch (err) {
      console.error('Errore durante il caricamento dei dati dei terapeuti:', err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> In attesa</span>;
      case 'confirmed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Confermato</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Completato</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs flex items-center"><XCircle className="w-3 h-3 mr-1" /> Cancellato</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">{status}</span>;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-4 border-purple-500 rounded-full border-t-transparent"></div></div>;
  }

  if (error) {
    return <div className="bg-red-50 p-4 rounded-md text-red-700">{error}</div>;
  }

  if (appointments.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg text-center">
        <p className="text-gray-500">Nessun appuntamento trovato per questo paziente.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        {appointments.map((appointment) => {
          const therapist = therapists[appointment.therapist_id] || {};
          
          return (
            <div key={appointment.id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-medium">{appointment.service_name || 'Appuntamento'}</h3>
                  <div className="flex items-center mt-1 text-gray-600">
                    <User className="w-4 h-4 mr-2" />
                    <span>
                      {therapist.first_name} {therapist.last_name} 
                      {therapist.specialization ? ` - ${therapist.specialization}` : ''}
                    </span>
                  </div>
                </div>
                {getStatusBadge(appointment.status)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{formatDate(appointment.appointment_date)}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>{formatTime(appointment.appointment_date)} - {formatTime(appointment.end_time)}</span>
                </div>
              </div>
              
              {appointment.feedback && (
                <div className="mt-3 pt-3 border-t">
                  <h4 className="text-sm font-medium mb-1">Feedback del paziente:</h4>
                  <p className="text-sm text-gray-600">{appointment.feedback}</p>
                  {appointment.feedback_rating && (
                    <div className="mt-1 flex items-center">
                      <span className="text-sm text-gray-500 mr-2">Valutazione:</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <svg 
                            key={i} 
                            className={`w-4 h-4 ${i < appointment.feedback_rating ? 'text-yellow-400' : 'text-gray-300'}`} 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {appointment.price && (
                <div className="mt-3 text-right">
                  <span className="font-medium">{appointment.price} €</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

AdminPatientAppointments.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default AdminPatientAppointments;